import {
  Box,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  Badge,
} from "@chakra-ui/react";
import {
  FaCalendarAlt,
  FaBell,
  FaClock,
  FaStar,
  FaHome,
  FaUsers,
  FaClipboardList,
  FaUser,
} from "react-icons/fa";
import { MotionBox, TEAL, TEAL_DARK, TEAL_LIGHT } from "./utils";

const DashboardMockup = () => {
    const statCards = [
      {
        value: "130",
        label: "Összes foglalás",
        iconBg: TEAL,
        iconColor: "white",
        icon: FaCalendarAlt,
      },
      {
        value: "32",
        label: "Összes ügyfél",
        iconBg: "#4BA3E3",
        iconColor: "white",
        icon: FaUsers,
      },
      {
        value: "5",
        label: "Összes szolgáltatás",
        iconBg: "#F4735A",
        iconColor: "white",
        icon: FaClipboardList,
      },
      {
        value: "4.98",
        label: "Értékelés (82 db)",
        iconBg: "#4CC98A",
        iconColor: "white",
        icon: FaStar,
      },
    ];

    const events = [
      {
        name: "Kovács Bettina",
        sub: "Hidratáló arckezelés • Pécs",
        time: "10:00 – 10:45",
        tag: "egyéni",
        tagBg: TEAL,
        group: false,
        count: 0,
      },
      {
        name: "Stresszoldó workshop",
        sub: "16 résztvevő • Workshop",
        time: "13:00 – 14:45",
        tag: "csoportos",
        tagBg: "#F4A12B",
        group: true,
        count: 16,
      },
      {
        name: "Nagy Anna",
        sub: "Lávaköves masszázs • Budapest",
        time: "15:00 – 16:35",
        tag: "egyéni",
        tagBg: TEAL,
        group: false,
        count: 0,
      },
    ];

    return (
      <Box position="relative" w="full" maxW="540px" mx="auto">
        {/* ambient glow */}
        <Box
          position="absolute"
          top="10%"
          left="50%"
          transform="translateX(-50%)"
          w="70%"
          h="60%"
          borderRadius="50%"
          bg={`${TEAL_LIGHT}22`}
          filter="blur(52px)"
          pointerEvents="none"
        />

        {/* outer shell — mimics the dashed purple border in the screenshot */}
        <Box
          bg="white"
          borderRadius="2xl"
          p={5}
          boxShadow={`0 28px 64px ${TEAL_DARK}1c, 0 4px 18px ${TEAL}14`}
          border="1.5px solid"
          borderColor="gray.100"
          position="relative"
          zIndex={1}
        >
          {/* ── greeting ── */}
          <Text
            fontFamily="'Bricolage Grotesque', sans-serif"
            fontWeight={800}
            fontSize="1.5rem"
            letterSpacing="-0.02em"
            bgGradient={`linear(to-r, ${TEAL}, ${TEAL_LIGHT})`}
            bgClip="text"
            mb={4}
          >
            Szia Anna 👋
          </Text>

          {/* ── quick-link buttons ── */}
          <HStack gap={3} mb={5}>
            {[
              { icon: FaCalendarAlt, label: "Foglalási oldal" },
              { icon: FaHome, label: "Weboldal" },
            ].map((btn, i) => (
              <HStack
                key={i}
                gap={2}
                px={4}
                py={2}
                bg="white"
                border="1.5px solid"
                borderColor="gray.200"
                borderRadius="xl"
                boxShadow="sm"
                cursor="pointer"
                _hover={{ borderColor: TEAL_LIGHT, boxShadow: "md" }}
                transition="all 0.18s"
              >
                <Icon as={btn.icon} color={TEAL} boxSize={3.5} />
                <Text
                  fontSize="xs"
                  fontWeight={700}
                  color={TEAL_DARK}
                  textDecoration="underline"
                >
                  {btn.label}
                </Text>
              </HStack>
            ))}
          </HStack>

          {/* ── stat cards ── */}
          <SimpleGrid columns={4} gap={2} mb={5}>
            {statCards.map((s, i) => (
              <Box
                key={i}
                bg="white"
                border="1px solid"
                borderColor="gray.100"
                borderRadius="xl"
                p={3}
                boxShadow="sm"
              >
                <Box
                  w={8}
                  h={8}
                  borderRadius="lg"
                  bg={s.iconBg}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                >
                  <Icon as={s.icon} color={s.iconColor} boxSize={3.5} />
                </Box>
                <Text
                  fontWeight={800}
                  fontSize="lg"
                  color={TEAL_DARK}
                  letterSpacing="-0.02em"
                  lineHeight={1}
                >
                  {s.value}
                </Text>
                <Text fontSize="9px" color="gray.400" mt={0.5} lineHeight={1.3}>
                  {s.label}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          {/* ── today's events ── */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.100"
            borderRadius="xl"
            p={4}
            boxShadow="sm"
          >
            <Text fontWeight={700} fontSize="sm" color={TEAL_DARK} mb={3}>
              Mai események
            </Text>
            <Stack gap={3}>
              {events.map((ev, i) => (
                <HStack key={i} gap={3} align="center">
                  {/* avatar */}
                  <Box position="relative" flexShrink={0}>
                    <Box
                      w={9}
                      h={9}
                      borderRadius="full"
                      bg={ev.group ? `${TEAL_LIGHT}33` : `${TEAL}22`}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon
                        as={ev.group ? FaUsers : FaUser}
                        color={TEAL}
                        boxSize={3.5}
                      />
                    </Box>
                    {ev.group && ev.count > 0 && (
                      <Badge
                        position="absolute"
                        top="-4px"
                        right="-4px"
                        bg="red.500"
                        color="white"
                        borderRadius="full"
                        fontSize="8px"
                        minW="16px"
                        h="16px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight={800}
                      >
                        {ev.count}
                      </Badge>
                    )}
                  </Box>

                  {/* info */}
                  <Box flex={1} minW={0}>
                    <Text
                      fontSize="xs"
                      fontWeight={700}
                      color={TEAL_DARK}
                      noOfLines={1}
                    >
                      {ev.name}
                    </Text>
                    <Text fontSize="10px" color="gray.400" noOfLines={1}>
                      {ev.sub}
                    </Text>
                  </Box>

                  {/* time + tag */}
                  <VStack gap={1} align="end" flexShrink={0}>
                    <HStack gap={1}>
                      <Icon as={FaClock} color={TEAL_LIGHT} boxSize={2.5} />
                      <Text fontSize="10px" fontWeight={600} color="gray.500">
                        {ev.time}
                      </Text>
                    </HStack>
                    <Badge
                      bg={ev.tagBg}
                      color="white"
                      borderRadius="full"
                      px={2}
                      py="1px"
                      fontSize="9px"
                      fontWeight={700}
                    >
                      {ev.tag}
                    </Badge>
                  </VStack>
                </HStack>
              ))}
            </Stack>
          </Box>
        </Box>

        {/* floating new-booking toast */}
        <MotionBox
          position="absolute"
          bottom={-4}
          right={-3}
          bg="white"
          borderRadius="xl"
          p="9px 13px"
          boxShadow={`0 8px 28px ${TEAL_DARK}22`}
          border="1px solid"
          borderColor="gray.100"
          zIndex={2}
          animate={{ y: [0, -7, 0] }}
          transition={
            { duration: 3, repeat: Infinity, ease: "easeInOut" } as any
          }
        >
          <HStack gap={2}>
            <Box
              w={7}
              h={7}
              bg={`${TEAL_LIGHT}22`}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={TEAL}
            >
              <Icon as={FaBell} boxSize={3} />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight={700} color={TEAL_DARK}>
                Új foglalás!
              </Text>
              <Text fontSize="10px" color="gray.400">
                Anna: 15:00-ra foglalt
              </Text>
            </Box>
          </HStack>
        </MotionBox>
      </Box>
    );
  };

export default DashboardMockup;
